import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListItem, MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { map, Observable } from 'rxjs';

export interface IMultiselectListItem {
  id: number;
  label: string;
  data?: any;
}

@Component({
  selector: 'app-multi-select-autocomplete',
  imports: [MatButtonModule, MatListModule, MatIconModule, MatProgressSpinnerModule, FormsModule],
  templateUrl: './multi-select-autocomplete.component.html',
  styleUrl: './multi-select-autocomplete.component.scss',
})
export class MultiSelectAutocompleteComponent implements OnChanges {
  //#region Properties & Inputs
  @Input() key?: string;
  @Input() queryFunction?: (query: string) => Observable<IMultiselectListItem[]>;
  @Input() allowMultiple = true;
  @Input() initialSelectedValues?: IMultiselectListItem[];
  @Input() valid: boolean = true
  @Output() selectionChange = new EventEmitter<IMultiselectListItem[]>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchResultsContainer') searchResultsContainer?: ElementRef<HTMLElement>;
  @ViewChild('selectedItemsContainer') selectedItemsContainer?: ElementRef<HTMLElement>;
  @ViewChildren('searchResultItem') searchResultItems?: QueryList<MatListItem>;
  @ViewChildren('selectedListItem') selectedListItems?: QueryList<MatListItem>;

  // Component state
  isOpen = false;
  query = '';
  selected: IMultiselectListItem[] = [];
  isLoading = false;
  queryQueued: any;

  // Private properties
  private _results: IMultiselectListItem[] = [];
  private queryDelay = 300;

  // Keyboard navigation properties
  focusedList: 'search' | 'selected' | null = null;
  focusedIndex = -1;
  isUsingKeyboard = false;
  //#endregion

  //#region Constructor & Lifecycle
  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allowMultiple'] && !changes['allowMultiple'].isFirstChange()) {
      if (!changes['allowMultiple'].currentValue && this.selected.length > 1) {
        // If switching to single selection mode, keep only the first selected item
        this.selected = [this.selected[0]];
      }
    }

    if (changes['key'] && !changes['key'].isFirstChange()) {
      // Clear existing selection and query when key changes
      this.selected = [];
      this.query = '';
      this._results = [];
      this.selectionChange.emit(this.selected);
    }

    if (
      changes['initialSelectedValues'] &&
      changes['initialSelectedValues'].currentValue &&
      changes['initialSelectedValues'].isFirstChange()
    ) {
      this.selected = [...changes['initialSelectedValues'].currentValue];

      // Try to load the labels for the initially selected values if a query function is provided
      if (this.queryFunction && this.selected.length > 0) {
        const idsToFetch = this.selected.map((item) => item.id).join(',');
        this.isLoading = true;
        this.queryFunction(idsToFetch)
          .pipe(
            map((result) => {
              // Map fetched results to the selected items to ensure labels are correct
              return this.selected.map((selected) => result.find((r) => r.id === selected.id) || selected);
            }),
          )
          .subscribe({
            next: (result) => {
              this.selected = result;
              this.isLoading = false;
            },
            error: (err) => {
              console.error('Error fetching initial selected values:', err);
              this.isLoading = false;
            },
          });
      }
    }
  }
  //#endregion

  //#region Data Management & Computed Properties
  get results() {
    return this._results.filter((r) => !this.selected.find((s) => s.id === r.id)).sort((a, b) => a?.label?.localeCompare(b?.label));
  }

  onQueryInput() {
    // Clear existing timeout
    if (this.queryQueued) {
      clearTimeout(this.queryQueued);
      this.queryQueued = null;
    }

    if (!this.query) {
      this.runQuery();
      return;
    }

    // Set new timeout
    this.queryQueued = setTimeout(() => {
      this.runQuery();
      this.queryQueued = null;
    }, this.queryDelay);
  }

  runQuery() {
    if (!this.queryFunction) {
      return;
    }
    this.isLoading = true;
    this.queryFunction(this.query)
      .pipe(map((res) => res.filter((r) => !this.selected.find((s) => s.id === r.id))))
      .subscribe({
        next: (res) => {
          this._results = res;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching results:', err);
          this.isLoading = false;
        },
      });
  }
  //#endregion

  //#region Keyboard Navigation
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.isOpen) {
      return;
    }

    this.isUsingKeyboard = true;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateDown();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateUp();
        break;
      case 'Enter':
        event.preventDefault();
        this.selectCurrentItem();
        break;
      case 'Tab':
        this.handleTabNavigation(event);
        break;
      case 'Escape':
        this.clearFocus();
        break;
    }
  }

  private navigateDown() {
    const currentList = this.getCurrentList();
    if (!currentList || currentList.length === 0) {
      return;
    }

    if (this.focusedIndex < currentList.length - 1) {
      this.focusedIndex++;
    } else {
      this.focusedIndex = 0; // Loop to top
    }

    this.scrollToFocusedItem();
  }

  private navigateUp() {
    const currentList = this.getCurrentList();
    if (!currentList || currentList.length === 0) {
      return;
    }

    if (this.focusedIndex > 0) {
      this.focusedIndex--;
    } else {
      this.focusedIndex = currentList.length - 1; // Loop to bottom
    }

    this.scrollToFocusedItem();
  }

  private selectCurrentItem() {
    if (this.focusedList === 'search' && this.focusedIndex >= 0) {
      const item = this.results[this.focusedIndex];
      if (item) {
        this.selectItem(item.id);
      }
    } else if (this.focusedList === 'selected' && this.focusedIndex >= 0) {
      const item = this.selected[this.focusedIndex];
      if (item) {
        this.removeSelected(item.id);
      }
    }
  }

  private handleTabNavigation(event: KeyboardEvent) {
    if (this.focusedList === 'search') {
      if (this.selected.length > 0) {
        event.preventDefault();
        this.focusedList = 'selected';
        this.focusedIndex = 0;
      }
    } else if (this.focusedList === 'selected') {
      if (!event.shiftKey && this.results.length > 0) {
        event.preventDefault();
        this.focusedList = 'search';
        this.focusedIndex = 0;
      }
    }
  }

  private getCurrentList(): IMultiselectListItem[] {
    if (this.focusedList === 'search') {
      return this.results;
    } else if (this.focusedList === 'selected') {
      return this.selected;
    }
    return [];
  }

  private clearFocus() {
    this.focusedList = null;
    this.focusedIndex = -1;
    this.isUsingKeyboard = false;
  }

  onSearchResultsFocus() {
    this.focusedList = 'search';
    this.focusedIndex = this.results.length > 0 ? 0 : -1;
    if (this.focusedIndex >= 0 && this.isUsingKeyboard) {
      this.scrollToFocusedItem();
    }
  }

  onSelectedItemsFocus() {
    this.focusedList = 'selected';
    this.focusedIndex = this.selected.length > 0 ? 0 : -1;
    if (this.focusedIndex >= 0) {
      this.scrollToFocusedItem();
    }
  }

  isItemFocused(list: 'search' | 'selected', index: number): boolean {
    return this.isUsingKeyboard && this.focusedList === list && this.focusedIndex === index;
  }

  private scrollToFocusedItem() {
    // Use setTimeout to ensure the DOM has been updated after the focus change
    setTimeout(() => {
      let itemElements: MatListItem[] = [];
      let container: ElementRef<HTMLElement> | undefined;

      if (this.focusedList === 'search' && this.searchResultItems && this.searchResultsContainer) {
        itemElements = this.searchResultItems.toArray();
        container = this.searchResultsContainer;
      } else if (this.focusedList === 'selected' && this.selectedListItems && this.selectedItemsContainer) {
        itemElements = this.selectedListItems.toArray();
        container = this.selectedItemsContainer;
      }

      if (itemElements.length > 0 && container) {
        const focusedElement = itemElements[this.focusedIndex];
        const nativeElement = focusedElement?._elementRef?.nativeElement;
        if (nativeElement) {
          this.scrollIntoViewIfNeeded(nativeElement, container.nativeElement);
        }
      }
    }, 50);
  }

  private scrollIntoViewIfNeeded(element: HTMLElement, container: HTMLElement) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Check if element is above the visible area
    if (elementRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - elementRect.top;
    } else if (elementRect.bottom > containerRect.bottom) {
      container.scrollTop += elementRect.bottom - containerRect.bottom;
    }
  }
  //#endregion

  //#region Mouse Interactions
  onMouseEnter() {
    if (this.isUsingKeyboard) {
      this.clearFocus();
    }
  }

  onMouseMove() {
    if (this.isUsingKeyboard) {
      this.isUsingKeyboard = false;
      this.focusedList = null;
      this.focusedIndex = -1;
    }
  }

  onFocusOut(event: FocusEvent) {
    // Use setTimeout to allow the browser to update the activeElement
    setTimeout(() => {
      const currentActiveElement = document.activeElement;
      const stillInside = currentActiveElement && this.host.nativeElement.contains(currentActiveElement);

      // If no active element is inside the component, close it
      if (!stillInside) {
        this.close();
      }
    }, 0);
  }
  //#endregion

  //#region Selection Management
  selectItem(id: number) {
    const item = this.results.find((r) => r.id === id);
    if (item && !this.selected.find((s) => s.id === item.id)) {
      if (!this.allowMultiple) {
        // Clear existing selection if single selection mode
        this.selected = [item];
      } else {
        this.selected.push(item);
      }
      this.query = '';
      this.selectionChange.emit(this.selected);
    }
  }

  removeSelected(id: number) {
    this.selected = this.selected.filter((s) => s.id !== id);
    this.selectionChange.emit(this.selected);
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    }, 0);
  }

  removeAll() {
    this.selected = [];
    this.selectionChange.emit(this.selected);
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    }, 0);
  }

  addAll() {
    if (!this.allowMultiple) return;
    this.selected = [...this.selected, ...this.results];
    this.query = '';
    this.selectionChange.emit(this.selected);
  }
  //#endregion

  //#region Utility Methods
  open() {
    this.isOpen = true;
    if (this._results.length === 0 && !this.query) {
      this.runQuery();
    }
  }

  close() {
    this.isOpen = false;
  }

  getItemDisplayLabel(item: IMultiselectListItem): string {
    if (!item) {
      return '';
    }
    return item.label ? `${item.label} (${item.id})` : `${item.id}`;
  }

  getPlaceholder() {
    if (this.selected.length === 0) {
      return 'Search and select...';
    }
    let placeholder = `Selected: ${this.getItemDisplayLabel(this.selected[0])}`;
    if (this.selected.length > 1) {
      placeholder += ` (+${this.selected.length - 1} more)`;
    }
    return placeholder;
  }
  //#endregion
}
